import React, { useState, useEffect } from "react";
import useStyles from "./style";
import {
  Dialog,
  Typography,
  Button,
  IconButton,
  Grid,
  MenuItem,
  TextField,
  InputAdornment,
  Box,
} from "@mui/material";
import {
  CloseOutlined,
  EditOutlined,
  PersonAddAltOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import _ from "lodash";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import * as yup from "yup";

//Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch ,useSelector} from "react-redux";

//Services
import CorporateApi from "Services/Corporate";

//Data
import { getCountryCodes } from "Utils/country";

const CorporateCreate = (props) => {
  const { openDialog, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );
  const [passengerimage, setpassengerimage] = useState("/assets/images/user/avatar.png");
  const [countryCodeData, setCountryCodeData] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const maxFileSize = 5242880;

  const fileLengthValidator = (file) => {
    if (file.size > maxFileSize) {
      alert("File size is larger than 5MB");
      return { code: "file-too-large", message: "File too large" };
    }
    return null;
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    accept: "image/*",
    validator: fileLengthValidator,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result;
          formik.setFieldValue("passengerProfileImage", file);
          setpassengerimage(base64String);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  useEffect(() => {
    try {
      const countries = getCountryCodes();
      setCountryCodeData(countries);
    } catch (error) {
      console.error("Error fetching country codes:", error);
    }
  }, []);

  const handleAddCorporate = (formValues) => {
    dispatch(showLoader("Loading please wait..."));
    const data = {
      firstName: formValues?.firstName,
      displayName: formValues?.displayName,
      email: formValues?.email,
      secondaryEmail: formValues?.secondaryEmail,
      password: formValues?.password,
      passConfirmation: formValues?.passConfirmation,
      panNumber: formValues?.panNumber,
      country: formValues?.country,
      mobileNumber: formValues?.mobile,
      address: formValues?.address,
      smsMailenable: formValues?.smsMailenable,
      image: formValues?.passengerProfileImage,
      demoaccount_id: demoaccount_id || "",
    };

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Corporate Created Successfully",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "success",
          })
        );
        formik.resetForm();
        setpassengerimage("/assets/images/user/avatar.png");
        handleClose();
      }
    };

    const onFailure = (err) => {
      let messages;
      const messagesArray = err?.response?.data?.data || [];
      if (messagesArray.length > 0) {
        messages = messagesArray.map((item) => item.msg).join(", ");
      } else {
        messages = err?.response?.data?.message;
      }
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: messages || "Error while creating corporate",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };

    CorporateApi.addCorporate({ ...data }).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      displayName: "",
      email: "",
      secondaryEmail: "",
      password: "",
      passConfirmation: "",
      panNumber: "",
      country: "",
      mobile: "",
      address: "",
      smsMailenable: false,
      passengerProfileImage: "",
    },
    validationSchema: yup.object({
      firstName: yup.string().required("First Name is required").nullable(),
      displayName: yup.string().required("Display Name is required").nullable(),
      email: yup.string().email("Invalid email format").required("Email is required").nullable(),
      secondaryEmail: yup.string().email("Invalid email format").required("Secondary Email is required").nullable(),
      mobile: yup
        .string()
        .matches(/^\d{1,15}$/, "Invalid mobile number")
        .required("Mobile is required")
        .nullable(),
      password: yup
        .string()
        .min(6, "Password must be at least 6 characters")
        .required("New password is required")
        .nullable(),
      passConfirmation: yup
        .string()
        .required("Confirm password is required")
        .oneOf([yup.ref("password"), null], "Passwords must match")
        .nullable(),
      panNumber: yup.string().required("PAN Number is required").nullable(),
      country: yup.string().required("Country is required").nullable(),
      address: yup.string().required("Address is required").nullable(),
      smsMailenable: yup.boolean().required("Choose enable or disable").nullable(),
      passengerProfileImage: yup.mixed().required("Corporate Logo is required").nullable(),
    }),
    enableReinitialize: true,
    onSubmit: handleAddCorporate,
  });

  return (
    <Dialog
      open={openDialog}
      onClose={() => {
        formik.resetForm();
        handleClose();
      }}
      classes={{ paper: classes.dialogPaper }}
      maxWidth={false}
    >
      {/* Header */}
      <Box className={classes.dialogHeader}>
        <Typography className={classes.dialogHeaderTitle}>Add Corporate</Typography>
        <IconButton className={classes.closeIconBtn} onClick={handleClose}>
          <CloseOutlined />
        </IconButton>
      </Box>

      {/* Body */}
      <Box className={classes.dialogBody}>
        {/* Left – Profile Photo */}
        <Box className={classes.leftColumn}>
          <Box className={classes.avatarWrapper}>
            <img
              src={passengerimage}
              alt="Profile"
              className={classes.avatarImg}
            />
            <Box
              component="span"
              className={classes.editIconOverlay}
              {...getRootProps()}
              onClick={open}
            >
              <input {...getInputProps()} />
              <EditOutlined />
            </Box>
          </Box>
          <Typography className={classes.photoLabel}>Corporate Logo</Typography>
          <Typography className={classes.photoSubLabel}>JPG, PNG up to 5MB</Typography>
          {formik.errors.passengerProfileImage && formik.touched.passengerProfileImage && (
            <Typography color="error" variant="caption" style={{ marginTop: 4 }}>
              {formik.errors.passengerProfileImage}
            </Typography>
          )}
        </Box>

        {/* Right – Form */}
        <Box className={classes.rightColumn}>
          <Grid container spacing={2}>
            {/* First Name + Display Name */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Legal Name</Typography>
              <TextField
                id="firstName"
                name="firstName"
                placeholder="e.g. Acme Corp"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Display Name</Typography>
              <TextField
                id="displayName"
                name="displayName"
                placeholder="e.g. Acme Inc"
                value={formik.values.displayName}
                onChange={formik.handleChange}
                error={formik.touched.displayName && Boolean(formik.errors.displayName)}
                helperText={formik.touched.displayName && formik.errors.displayName}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>

            {/* Primary Email + Secondary Email */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Primary Email</Typography>
              <TextField
                id="email"
                name="email"
                placeholder="corporate@example.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Secondary Email</Typography>
              <TextField
                id="secondaryEmail"
                name="secondaryEmail"
                placeholder="info@example.com"
                value={formik.values.secondaryEmail}
                onChange={formik.handleChange}
                error={formik.touched.secondaryEmail && Boolean(formik.errors.secondaryEmail)}
                helperText={formik.touched.secondaryEmail && formik.errors.secondaryEmail}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>

            {/* Mobile & PAN Number */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Mobile Number</Typography>
              <TextField
                name="mobile"
                id="mobile"
                placeholder="000 000 0000"
                value={formik.values.mobile}
                onChange={formik.handleChange}
                error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                helperText={formik.touched.mobile && formik.errors.mobile}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>PAN / Tax ID Number</Typography>
              <TextField
                name="panNumber"
                id="panNumber"
                placeholder="Enter PAN Number"
                value={formik.values.panNumber}
                onChange={formik.handleChange}
                error={formik.touched.panNumber && Boolean(formik.errors.panNumber)}
                helperText={formik.touched.panNumber && formik.errors.panNumber}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>

            {/* Password & Confirm Password */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Password</Typography>
              <TextField
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Confirm Password</Typography>
              <TextField
                id="passConfirmation"
                name="passConfirmation"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm password"
                value={formik.values.passConfirmation}
                onChange={formik.handleChange}
                error={formik.touched.passConfirmation && Boolean(formik.errors.passConfirmation)}
                helperText={formik.touched.passConfirmation && formik.errors.passConfirmation}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Country & Address */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Country</Typography>
              <TextField
                name="country"
                id="country"
                select
                value={formik.values.country || ""}
                onChange={formik.handleChange}
                error={formik.touched.country && Boolean(formik.errors.country)}
                helperText={formik.touched.country && formik.errors.country}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) => v || <span style={{ color: "#9ca3af" }}>Select country</span>,
                }}
              >
                {countryCodeData.map((country) => (
                  <MenuItem key={country.code} value={country.name}>
                    {`${country.code} (${country.name})`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>SMS / Email Notifications</Typography>
              <TextField
                name="smsMailenable"
                id="smsMailenable"
                select
                value={formik.values.smsMailenable}
                onChange={formik.handleChange}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              >
                <MenuItem value={true}>Enabled</MenuItem>
                <MenuItem value={false}>Disabled</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Typography className={classes.formLabel}>Address</Typography>
              <TextField
                name="address"
                id="address"
                placeholder="Enter address"
                value={formik.values.address}
                onChange={formik.handleChange}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Footer */}
      <Box className={classes.dialogFooter}>
        <Button
          className={classes.cancelButton}
          onClick={() => {
            formik.resetForm();
            handleClose();
          }}
        >
          Cancel
        </Button>
        <Button
          className={classes.submitButton}
          onClick={formik.handleSubmit}
          startIcon={<PersonAddAltOutlined />}
        >
          Create  Corporate
        </Button>
      </Box>
    </Dialog>
  );
};

export default CorporateCreate;
